import React, { useEffect, useMemo, useState } from "react";
import { pdf } from "@react-pdf/renderer";

import {
    Button,
    Card,
    Col,
    Form,
    Input,
    InputNumber,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Table,
    Tag,
    Tooltip,
    Typography,
    Popconfirm,
    Empty,
    Divider,
    Alert
} from "antd";

import {
    SearchOutlined,
    EditOutlined,
    DeleteOutlined,
    PlusOutlined,
    DollarOutlined,
    BarChartOutlined,
    DownloadOutlined,
    ReloadOutlined,
    CheckCircleOutlined,
    WalletOutlined,
    BookOutlined,
    SettingOutlined
} from "@ant-design/icons";

import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
    Legend,
    Cell,
    CartesianGrid
} from "recharts";

import Swal from "sweetalert2";

import api from "../api/api";
import { useAcademicYear } from "../AcademicYearContext";
import InformationSheetPDF from "./InformationSheetPDF";

const { Title, Text } = Typography;

const { Option } = Select;

const ORANGE = "#ff7900";
const BLACK = "#111111";
const NAVY = "#062A4B";
const GOLD = "#F6C944";
const GREEN = "#16803C";
const RED = "#C2410C";

const STUDENT_TYPES = [
    {
        value: "old",
        label: "Old Student"
    },
    {
        value: "new",
        label: "New Student"
    },

    {
        value: "scholarship",
        label: "Scholarship Student"
    },
    {
        value: "dependant_student",
        label: "Dependant Student"
    },
    {
        value: "ward",
        label: "Ward Student"
    }
];

const INITIAL_FORM_VALUES = {
    grade_class: undefined,
    student_type: undefined,
    yearly_school_fee: undefined,
    registration: undefined,
    first_installment: undefined,
    second_installment: undefined,
    third_installment: undefined,
    fourth_installment: undefined
};

const SchoolFeesData = () => {
    const [form] = Form.useForm();

    const { selectedYear } = useAcademicYear();

    const [gradeClasses, setGradeClasses] = useState([]);
    const [feesData, setFeesData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);

    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");

    const [page, setPage] = useState(1);
    const [rowsPerPage] = useState(10);

    const [editData, setEditData] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [activeTab, setActiveTab] = useState("records");

    const normalizeType = (value) => {
        if (!value) {
            return "";
        }

        return String(value)
            .trim()
            .toLowerCase();
    };

    const getGradeClassId = (value) => {
        if (
            value &&
            typeof value === "object"
        ) {
            return value.id;
        }

        return value;
    };

    const getGradeClassName = (value) => {
        if (
            value &&
            typeof value === "object"
        ) {
            return value.name || "N/A";
        }

        const matched = gradeClasses.find(
            (item) =>
                Number(item.id) ===
                Number(value)
        );

        return matched?.name || "N/A";
    };

    const getStudentTypeLabel = (type) => {
        const normalized = normalizeType(type);

        const found = STUDENT_TYPES.find(
            (item) =>
                item.value === normalized
        );

        return (
            found?.label ||
            `${normalized || "Unknown"} Student`
        );
    };

    const money = (value) => {
        const amount = Number(value || 0);

        return `$${amount.toLocaleString(
            undefined,
            {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
            }
        )}`;
    };

    const getTypeColor = (type) => {
        const normalized = normalizeType(type);

        if (normalized === "new") {
            return {
                color: "#111111",
                background: "#ff7900",
                borderColor: "#ff7900"
            };
        }

        if (normalized === "old") {
            return {
                color: "#ffffff",
                background: "#111111",
                borderColor: "#111111"
            };
        }

        if (normalized === "scholarship") {
            return {
                color: "#ffffff",
                background: "#001f5b",
                borderColor: "#001f5b"
            };
        }

          if (normalized === "dependant_student") {
            return {
                color: "#ffffff",
                background: "#001f5b",
                borderColor: "#001f5b"
            };
        } 

        if (normalized === "ward") {
            return {
                color: "#ffffff",
                background: "#16803c",
                borderColor: "#16803c"
            };
        }

        return {
            color: "#475569",
            background: "#f1f5f9",
            borderColor: "#e2e8f0"
        };
    };

    useEffect(() => {
        if (!selectedYear) {
            setGradeClasses([]);
            setFeesData([]);
            setFilteredData([]);
            return;
        }

        let mounted = true;

        const loadData = async () => {
            try {
                setLoading(true);

                const classRes = await api.get(
                    "/api/users/grade-classes/"
                );

                const classes =
                    classRes.data?.results ||
                    classRes.data ||
                    [];

                if (!mounted) {
                    return;
                }

                setGradeClasses(
                    Array.isArray(classes)
                        ? classes
                        : []
                );

                const feesRes = await api.get(
                    "/api/users/school-fees-data/",
                    {
                        params: {
                            academic_year:
                                selectedYear.id
                        }
                    }
                );

                const results =
                    feesRes.data?.results ||
                    feesRes.data ||
                    [];

                const safeResults =
                    Array.isArray(results)
                        ? results
                        : [];

                const mapped =
                    safeResults.map(
                        (item) => {
                            const matchedClass =
                                classes.find(
                                    (cls) =>
                                        Number(
                                            cls.id
                                        ) ===
                                        Number(
                                            item.grade_class
                                        )
                                );

                            return {
                                ...item,
                                grade_class:
                                    matchedClass ||
                                    item.grade_class
                            };
                        }
                    );

                if (!mounted) {
                    return;
                }

                setFeesData(mapped);
                setFilteredData(mapped);
            } catch (err) {
                console.error(
                    "Error loading school fees data:",
                    err
                );

                if (mounted) {
                    Swal.fire({
                        icon: "error",
                        title: "Unable to Load Data",
                        text:
                            "Failed to load school fee information.",
                        confirmButtonColor:
                            ORANGE
                    });
                }
            } finally {
                if (mounted) {
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            mounted = false;
        };
    }, [selectedYear]);

    useEffect(() => {
        const search =
            searchText
                .trim()
                .toLowerCase();

        if (!search) {
            setFilteredData(feesData);
            setPage(1);
            return;
        }

        const filtered =
            feesData.filter(
                (item) => {
                    const gradeClass =
                        getGradeClassName(
                            item.grade_class
                        ).toLowerCase();

                    const studentType =
                        String(
                            item.student_type ||
                                ""
                        ).toLowerCase();

                    return (
                        gradeClass.includes(
                            search
                        ) ||
                        studentType.includes(
                            search
                        )
                    );
                }
            );

        setFilteredData(filtered);
        setPage(1);
    }, [
        feesData,
        searchText,
        gradeClasses
    ]);

    const downloadInformationSheet =
        async () => {
            if (!selectedYear) {
                Swal.fire({
                    icon: "warning",
                    title: "Academic Year Required",
                    text:
                        "Please select an academic year first.",
                    confirmButtonColor:
                        ORANGE
                });

                return;
            }

            try {
                const blob =
                    await pdf(
                        <InformationSheetPDF
                            fees={filteredData}
                            academicYear={
                                selectedYear
                            }
                        />
                    ).toBlob();

                const url =
                    URL.createObjectURL(
                        blob
                    );

                const link =
                    document.createElement(
                        "a"
                    );

                link.href = url;

                link.download =
                    "Class_Fees_Information_Sheet.pdf";

                document.body.appendChild(
                    link
                );

                link.click();

                document.body.removeChild(
                    link
                );

                URL.revokeObjectURL(
                    url
                );

                Swal.fire({
                    icon: "success",
                    title: "PDF Generated",
                    text:
                        "The fee information sheet has been downloaded.",
                    timer: 1800,
                    showConfirmButton: false
                });
            } catch (error) {
                console.error(
                    error
                );

                Swal.fire({
                    icon: "error",
                    title: "PDF Generation Failed",
                    text:
                        "Unable to generate the information sheet.",
                    confirmButtonColor:
                        ORANGE
                });
            }
        };

    const handleInstallmentChange = (
        field,
        value
    ) => {
        const values =
            form.getFieldsValue();

        const yearly =
            Number(
                values.yearly_school_fee
            ) || 0;

        const registration =
            Number(
                field === "registration"
                    ? value
                    : values.registration
            ) || 0;

        const first =
            Number(
                field ===
                    "first_installment"
                    ? value
                    : values.first_installment
            ) || 0;

        const second =
            Number(
                field ===
                    "second_installment"
                    ? value
                    : values.second_installment
            ) || 0;

        const third =
            Number(
                field ===
                    "third_installment"
                    ? value
                    : values.third_installment
            ) || 0;

        const remaining =
            yearly -
            registration -
            first -
            second -
            third;

        form.setFieldsValue({
            [field]: value,
            fourth_installment:
                remaining >= 0
                    ? Number(
                          remaining.toFixed(
                              2
                          )
                      )
                    : 0
        });
    };

    const openCreateModal = () => {
        setEditData(null);

        form.resetFields();

        form.setFieldsValue(
            INITIAL_FORM_VALUES
        );

        setModalOpen(true);
    };

    const handleEdit = (data) => {
        setEditData(data);

        form.setFieldsValue({
            grade_class:
                getGradeClassId(
                    data.grade_class
                ),

            student_type:
                normalizeType(
                    data.student_type
                ),

            yearly_school_fee:
                data.yearly_school_fee,

            registration:
                data.registration,

            first_installment:
                data.first_installment,

            second_installment:
                data.second_installment,

            third_installment:
                data.third_installment,

            fourth_installment:
                data.fourth_installment
        });

        setModalOpen(true);
    };

    const closeModal = () => {
        setModalOpen(false);

        setEditData(null);

        form.resetFields();
    };

    const handleSubmit = async (
        values
    ) => {
        if (!selectedYear) {
            Swal.fire({
                icon: "warning",
                title: "Select Academic Year",
                text:
                    "Please select an academic year first.",
                confirmButtonColor:
                    ORANGE
            });

            return;
        }

        const yearly =
            Number(
                values.yearly_school_fee
            ) || 0;

        const registration =
            Number(
                values.registration
            ) || 0;

        const first =
            Number(
                values.first_installment
            ) || 0;

        const second =
            Number(
                values.second_installment
            ) || 0;

        const third =
            Number(
                values.third_installment
            ) || 0;

        const fourth =
            Number(
                values.fourth_installment
            ) || 0;

        const totalInstallments =
            registration +
            first +
            second +
            third +
            fourth;

        if (yearly <= 0) {
            Swal.fire({
                icon: "warning",
                title: "Invalid Yearly Fee",
                text:
                    "The yearly school fee must be greater than zero.",
                confirmButtonColor:
                    ORANGE
            });

            return;
        }

        if (
            Math.abs(
                totalInstallments -
                    yearly
            ) > 0.01
        ) {
            Swal.fire({
                icon: "error",
                title: "Installment Mismatch",
                html:
                    `Total installments <strong>${money(
                        totalInstallments
                    )}</strong> must equal yearly fee <strong>${money(
                        yearly
                    )}</strong>.`,
                confirmButtonColor:
                    ORANGE
            });

            return;
        }

        const payload = {
            academic_year:
                selectedYear.id,

            grade_class:
                values.grade_class,

            student_type:
                values.student_type,

            yearly_school_fee:
                values.yearly_school_fee,

            registration:
                values.registration,

            first_installment:
                values.first_installment,

            second_installment:
                values.second_installment,

            third_installment:
                values.third_installment,

            fourth_installment:
                values.fourth_installment
        };

        setLoading(true);

        try {
            if (editData) {
                const res =
                    await api.put(
                        `/api/users/school-fees-data/${editData.id}/`,
                        payload
                    );

                const selectedClass =
                    gradeClasses.find(
                        (cls) =>
                            Number(
                                cls.id
                            ) ===
                            Number(
                                values.grade_class
                            )
                    );

                const updatedData = {
                    ...res.data,

                    grade_class:
                        selectedClass ||
                        res.data.grade_class
                };

                setFeesData(
                    (previous) =>
                        previous.map(
                            (item) =>
                                Number(
                                    item.id
                                ) ===
                                Number(
                                    editData.id
                                )
                                    ? updatedData
                                    : item
                        )
                );

                Swal.fire({
                    icon: "success",
                    title: "Fee Structure Updated",
                    text:
                        "The fee structure was updated successfully.",
                    confirmButtonColor:
                        ORANGE
                });
            } else {
                const exists =
                    feesData.some(
                        (item) =>
                            Number(
                                getGradeClassId(
                                    item.grade_class
                                )
                            ) ===
                                Number(
                                    payload.grade_class
                                ) &&
                            normalizeType(
                                item.student_type
                            ) ===
                                normalizeType(
                                    payload.student_type
                                )
                    );

                if (exists) {
                    const selectedType =
                        STUDENT_TYPES.find(
                            (type) =>
                                type.value ===
                                normalizeType(
                                    payload.student_type
                                )
                        );

                    Swal.fire({
                        icon: "warning",
                        title: "Duplicate Fee Structure",
                        text:
                            `This class or prohram group already has a fee structure for ${
                                selectedType?.label ||
                                payload.student_type
                            }.`,
                        confirmButtonColor:
                            ORANGE
                    });

                    return;
                }

                const res =
                    await api.post(
                        "/api/users/school-fees-data/",
                        payload
                    );

                const selectedClass =
                    gradeClasses.find(
                        (cls) =>
                            Number(
                                cls.id
                            ) ===
                            Number(
                                values.grade_class
                            )
                    );

                const updatedData = {
                    ...res.data,

                    grade_class:
                        selectedClass ||
                        res.data.grade_class
                };

                setFeesData(
                    (previous) => [
                        updatedData,
                        ...previous
                    ]
                );

                Swal.fire({
                    icon: "success",
                    title: "Fee Structure Created",
                    text:
                        "The new fee structure was created successfully.",
                    confirmButtonColor:
                        ORANGE
                });
            }

            closeModal();
        } catch (error) {
            console.error(
                "Error saving school fees data:",
                error.response?.data ||
                    error
            );

            const backendData =
                error.response?.data;

            let backendMessage =
                backendData?.detail ||
                backendData?.student_type ||
                backendData?.grade_class ||
                backendData?.error;

            if (
                Array.isArray(
                    backendMessage
                )
            ) {
                backendMessage =
                    backendMessage.join(
                        ", "
                    );
            }

            if (
                typeof backendMessage ===
                "object"
            ) {
                backendMessage =
                    JSON.stringify(
                        backendMessage
                    );
            }

            Swal.fire({
                icon: "error",
                title: "Save Failed",
                text:
                    backendMessage ||
                    "Failed to save fee structure.",
                confirmButtonColor:
                    ORANGE
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (
        id
    ) => {
        try {
            await api.delete(
                `/api/users/school-fees-data/${id}/`
            );

            setFeesData(
                (previous) =>
                    previous.filter(
                        (item) =>
                            Number(
                                item.id
                            ) !==
                            Number(id)
                    )
            );

            Swal.fire({
                icon: "success",
                title: "Record Deleted",
                text:
                    "The fee record has been deleted successfully.",
                confirmButtonColor:
                    ORANGE
            });
        } catch (error) {
            console.error(
                "Error deleting data:",
                error
            );

            Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text:
                    "Failed to delete the fee record.",
                confirmButtonColor:
                    ORANGE
            });
        }
    };

    const resetSearch = () => {
        setSearchText("");
        setPage(1);
    };

    const totalRecords =
        filteredData.length;

    const totalFees =
        filteredData.reduce(
            (sum, item) =>
                sum +
                Number(
                    item.yearly_school_fee ||
                        0
                ),
            0
        );

    const totalRegistration =
        filteredData.reduce(
            (sum, item) =>
                sum +
                Number(
                    item.registration ||
                        0
                ),
            0
        );

    const totalInstallments =
        filteredData.reduce(
            (sum, item) =>
                sum +
                Number(
                    item.first_installment ||
                        0
                ) +
                Number(
                    item.second_installment ||
                        0
                ) +
                Number(
                    item.third_installment ||
                        0
                ) +
                Number(
                    item.fourth_installment ||
                        0
                ),
            0
        );

    const averageFee =
        totalRecords > 0
            ? totalFees /
              totalRecords
            : 0;

    const chartData =
        filteredData.map(
            (item) => ({
                name:
                    getGradeClassName(
                        item.grade_class
                    ),

                fee: Number(
                    item.yearly_school_fee ||
                        0
                ),

                studentType:
                    getStudentTypeLabel(
                        item.student_type
                    )
            })
        );

    const chartHeight =
        Math.max(
            320,
            Math.min(
                520,
                chartData.length *
                    48 +
                    140
            )
        );

    const columns = useMemo(
        () => [
            {
                title: "CLASS / PROMGRAM GROUP",
                dataIndex: [
                    "grade_class",
                    "name"
                ],
                key: "grade_class",
                fixed: "left",
                width: 190,
                render: (
                    text,
                    record
                ) => (
                    <div className="fee-class-cell">
                        <div className="fee-class-icon">
                            <BookOutlined />
                        </div>

                        <div>
                            <Text strong>
                                {text ||
                                    getGradeClassName(
                                        record.grade_class
                                    ) ||
                                    "N/A"}
                            </Text>

                            <span>
                                Fee Structure
                            </span>
                        </div>
                    </div>
                )
            },

            {
                title: "STUDENT TYPE",
                dataIndex:
                    "student_type",
                key: "student_type",
                width: 190,
                render: (
                    type
                ) => {
                    const typeStyle =
                        getTypeColor(
                            type
                        );

                    return (
                        <Tag
                            style={{
                                ...typeStyle,
                                fontWeight: 800,
                                textTransform:
                                    "uppercase",
                                borderRadius: 20,
                                padding:
                                    "5px 12px",
                                whiteSpace:
                                    "nowrap"
                            }}
                        >
                            {getStudentTypeLabel(
                                type
                            )}
                        </Tag>
                    );
                }
            },

            {
                title: "YEARLY FEE",
                dataIndex:
                    "yearly_school_fee",
                key:
                    "yearly_school_fee",
                width: 155,
                render: (
                    value
                ) => (
                    <div className="fee-money-cell">
                        <span>
                            Yearly
                        </span>
                        <strong>
                            {money(value)}
                        </strong>
                    </div>
                )
            },

            {
                title: "REGISTRATION",
                dataIndex:
                    "registration",
                key:
                    "registration",
                width: 150,
                render: money
            },

            {
                title: "1ST INSTALLMENT",
                dataIndex:
                    "first_installment",
                key:
                    "first_installment",
                width: 155,
                render: money
            },

            {
                title: "2ND INSTALLMENT",
                dataIndex:
                    "second_installment",
                key:
                    "second_installment",
                width: 155,
                render: money
            },

            {
                title: "3RD INSTALLMENT",
                dataIndex:
                    "third_installment",
                key:
                    "third_installment",
                width: 155,
                render: money
            },

            {
                title: "4TH INSTALLMENT",
                dataIndex:
                    "fourth_installment",
                key:
                    "fourth_installment",
                width: 155,
                render: (
                    value
                ) => (
                    <Tag
                        color="green"
                        style={{
                            borderRadius: 8,
                            fontWeight: 800,
                            padding:
                                "4px 9px"
                        }}
                    >
                        {money(value)}
                    </Tag>
                )
            },

            {
                title: "ACTIONS",
                key: "actions",
                fixed: "right",
                width: 145,
                render: (
                    _,
                    record
                ) => (
                    <Space size={7}>
                        <Tooltip title="Edit fee structure">
                            <Button
                                type="primary"
                                icon={
                                    <EditOutlined />
                                }
                                onClick={() =>
                                    handleEdit(
                                        record
                                    )
                                }
                                className="fee-edit-button"
                            />
                        </Tooltip>

                        <Popconfirm
                            title="Delete this fee structure?"
                            description="This action cannot be undone."
                            onConfirm={() =>
                                handleDelete(
                                    record.id
                                )
                            }
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{
                                danger: true
                            }}
                        >
                            <Tooltip title="Delete fee structure">
                                <Button
                                    danger
                                    icon={
                                        <DeleteOutlined />
                                    }
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                )
            }
        ],
        [
            gradeClasses,
            filteredData
        ]
    );

    const renderStudentTypeSelect =
        (
            value,
            onChange,
            size = "large"
        ) => (
            <Select
                value={value}
                onChange={onChange}
                placeholder="Select student payment type"
                size={size}
                allowClear
                style={{
                    width: "100%"
                }}
            >
                {STUDENT_TYPES.map(
                    (type) => (
                        <Option
                            key={
                                type.value
                            }
                            value={
                                type.value
                            }
                        >
                            {type.label}
                        </Option>
                    )
                )}
            </Select>
        );

    const renderFeeForm = () => (
        <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            requiredMark={false}
        >
            <div className="fee-form-section">
                <div className="fee-form-section-title">
                    <span>01</span>
                    Fee Structure
                </div>

                <Row
                    gutter={[
                        16,
                        0
                    ]}
                >
                    <Col
                        xs={24}
                        sm={12}
                    >
                        <Form.Item
                            label="Class / Program Group"
                            name="grade_class"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Select class or Program group"
                                }
                            ]}
                        >
                            <Select
                                showSearch
                                placeholder="Select class or  Program group"
                                optionFilterProp="children"
                                size="large"
                            >
                                {gradeClasses.map(
                                    (
                                        grade
                                    ) => (
                                        <Option
                                            key={
                                                grade.id
                                            }
                                            value={
                                                grade.id
                                            }
                                        >
                                            {
                                                grade.name
                                            }
                                        </Option>
                                    )
                                )}
                            </Select>
                        </Form.Item>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                    >
                        <Form.Item
                            label="Student Payment Type"
                            name="student_type"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Select student payment type"
                                }
                            ]}
                        >
                            {renderStudentTypeSelect(
                                form.getFieldValue(
                                    "student_type"
                                ),
                                (
                                    value
                                ) =>
                                    form.setFieldsValue(
                                        {
                                            student_type:
                                                value
                                        }
                                    )
                            )}
                        </Form.Item>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                    >
                        <Form.Item
                            label="Yearly School Fee / Term Registration Fee"
                            name="yearly_school_fee"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Enter yearly fee"
                                }
                            ]}
                        >
                            <InputNumber
                                min={0}
                                precision={2}
                                prefix="$"
                                size="large"
                                style={{
                                    width: "100%"
                                }}
                                placeholder="0.00"
                            />
                        </Form.Item>
                    </Col>

                    <Col
                        xs={24}
                        sm={12}
                    >
                        <Form.Item
                            label="Registration Fee"
                            name="registration"
                            rules={[
                                {
                                    required:
                                        true,
                                    message:
                                        "Enter registration fee"
                                }
                            ]}
                        >
                            <InputNumber
                                min={0}
                                precision={2}
                                prefix="$"
                                size="large"
                                style={{
                                    width: "100%"
                                }}
                                placeholder="0.00"
                                onChange={(
                                    value
                                ) =>
                                    handleInstallmentChange(
                                        "registration",
                                        value
                                    )
                                }
                            />
                        </Form.Item>
                    </Col>
                </Row>
            </div>

            <div className="fee-form-section">
                <div className="fee-form-section-title">
                    <span>02</span>
                    Installment Schedule
                </div>

                <div className="fee-installment-grid">
                    <Form.Item
                        label="1st Installment"
                        name="first_installment"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Enter first installment"
                            }
                        ]}
                    >
                        <InputNumber
                            min={0}
                            precision={2}
                            prefix="$"
                            size="large"
                            style={{
                                width: "100%"
                            }}
                            placeholder="0.00"
                            onChange={(
                                value
                            ) =>
                                handleInstallmentChange(
                                    "first_installment",
                                    value
                                )
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="2nd Installment"
                        name="second_installment"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Enter second installment"
                            }
                        ]}
                    >
                        <InputNumber
                            min={0}
                            precision={2}
                            prefix="$"
                            size="large"
                            style={{
                                width: "100%"
                            }}
                            placeholder="0.00"
                            onChange={(
                                value
                            ) =>
                                handleInstallmentChange(
                                    "second_installment",
                                    value
                                )
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="3rd Installment"
                        name="third_installment"
                        rules={[
                            {
                                required:
                                    true,
                                message:
                                    "Enter third installment"
                            }
                        ]}
                    >
                        <InputNumber
                            min={0}
                            precision={2}
                            prefix="$"
                            size="large"
                            style={{
                                width: "100%"
                            }}
                            placeholder="0.00"
                            onChange={(
                                value
                            ) =>
                                handleInstallmentChange(
                                    "third_installment",
                                    value
                                )
                            }
                        />
                    </Form.Item>

                    <Form.Item
                        label="4th Installment"
                        name="fourth_installment"
                    >
                        <InputNumber
                            readOnly
                            prefix="$"
                            size="large"
                            style={{
                                width: "100%",
                                background:
                                    "#f5f7f9"
                            }}
                        />
                    </Form.Item>
                </div>
            </div>

            <div className="fee-calculation-note">
                <div className="fee-calculation-icon">
                    <CheckCircleOutlined />
                </div>

                <div>
                    <strong>
                        Automatic Balance Calculation
                    </strong>

                    <span>
                        The fourth installment is
                        automatically calculated from
                        the yearly school fee after
                        registration and installments
                        one through three.
                    </span>
                </div>
            </div>

            <div className="fee-form-actions">
                <Button
                    size="large"
                    onClick={closeModal}
                    className="fee-cancel-button"
                >
                    Cancel
                </Button>

                <Button
                    htmlType="submit"
                    type="primary"
                    size="large"
                    loading={loading}
                    icon={
                        editData ? (
                            <EditOutlined />
                        ) : (
                            <PlusOutlined />
                        )
                    }
                    className="fee-save-button"
                >
                    {editData
                        ? "Update Fee Structure"
                        : "Create Fee Structure"}
                </Button>
            </div>
        </Form>
    );

    return (
        <div className="school-fees-page">
            <div className="school-fees-shell">
                <section className="fees-hero">
                    <div className="fees-hero-main">
                        <div className="fees-hero-copy">
                            <div className="fees-kicker">
                                <span className="fees-kicker-dot" />
                                FINANCE MANAGEMENT
                            </div>

                            <div className="fees-title-row">
                                <div className="fees-title-icon">
                                    <DollarOutlined />
                                </div>

                                <div>
                                    <Title className="fees-title">
                                        School Fees Management
                                    </Title>

                                    <Text className="fees-subtitle">
                                        Configure, review and manage
                                        student fee structures for
                                        the active academic year.
                                    </Text>
                                </div>
                            </div>

                            <div className="fees-year-badge">
                                <span>
                                    ACTIVE ACADEMIC YEAR
                                </span>

                                <strong>
                                    {selectedYear?.name ||
                                        "None Selected"}
                                </strong>
                            </div>
                        </div>

                        <div className="fees-hero-actions">
                            <Button
                                icon={
                                    <DownloadOutlined />
                                }
                                className="fees-secondary-button"
                                onClick={
                                    downloadInformationSheet
                                }
                                disabled={
                                    filteredData.length ===
                                    0
                                }
                            >
                                Information Sheet
                            </Button>

                            <Button
                                icon={
                                    <PlusOutlined />
                                }
                                className="fees-primary-button"
                                onClick={
                                    openCreateModal
                                }
                            >
                                New Fee Structure
                            </Button>
                        </div>
                    </div>

                    <div className="fees-hero-strip">
                        <div>
                            <strong>
                                {totalRecords}
                            </strong>
                            <span>
                                Fee Structures
                            </span>
                        </div>

                        <div>
                            <strong>
                                {money(totalFees)}
                            </strong>
                            <span>
                                Total Yearly Fees
                            </span>
                        </div>

                        <div>
                            <strong>
                                {selectedYear?.name ||
                                    "Not Selected"}
                            </strong>
                            <span>
                                Current Academic Year
                            </span>
                        </div>
                    </div>
                </section>

                <div className="fees-summary-grid">
                    <Card
                        className="fees-summary-card"
                    >
                        <div className="fees-summary-icon orange">
                            <BarChartOutlined />
                        </div>

                        <div className="fees-summary-content">
                            <span>
                                Fee Records
                            </span>

                            <strong>
                                {totalRecords}
                            </strong>

                            <small>
                                Active filtered records
                            </small>
                        </div>
                    </Card>

                    <Card
                        className="fees-summary-card"
                    >
                        <div className="fees-summary-icon blue">
                            <WalletOutlined />
                        </div>

                        <div className="fees-summary-content">
                            <span>
                                Total Yearly Fees
                            </span>

                            <strong>
                                {money(totalFees)}
                            </strong>

                            <small>
                                Across displayed structures
                            </small>
                        </div>
                    </Card>

                    <Card
                        className="fees-summary-card"
                    >
                        <div className="fees-summary-icon green">
                            <CheckCircleOutlined />
                        </div>

                        <div className="fees-summary-content">
                            <span>
                                Scheduled Installments
                            </span>

                            <strong>
                                {money(
                                    totalInstallments
                                )}
                            </strong>

                            <small>
                                Excluding registration
                            </small>
                        </div>
                    </Card>

                    <Card
                        className="fees-summary-card"
                    >
                        <div className="fees-summary-icon gold">
                            <DollarOutlined />
                        </div>

                        <div className="fees-summary-content">
                            <span>
                                Average Yearly Fee
                            </span>

                            <strong>
                                {money(
                                    averageFee
                                )}
                            </strong>

                            <small>
                                Per fee structure
                            </small>
                        </div>
                    </Card>
                </div>

                <Card className="fees-control-card">
                    <div className="fees-section-heading">
                        <div>
                            <span className="fees-section-label">
                                RECORD MANAGEMENT
                            </span>

                            <Title level={4}>
                                Search & Controls
                            </Title>

                            <Text type="secondary">
                                Find fee structures by
                                 class, Program group or student type.
                            </Text>
                        </div>

                        <div className="fees-control-actions">
                            <Button
                                icon={
                                    <ReloadOutlined />
                                }
                                onClick={
                                    resetSearch
                                }
                                className="fees-reset-button"
                            >
                                Reset
                            </Button>

                            <Button
                                type="primary"
                                icon={
                                    <PlusOutlined />
                                }
                                onClick={
                                    openCreateModal
                                }
                                className="fees-create-button"
                            >
                                New Structure
                            </Button>
                        </div>
                    </div>

                    <div className="fees-filter-grid">
                        <div className="fees-search-field">
                            <Input
                                size="large"
                                allowClear
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder="Search class / Program group or student type..."
                                value={
                                    searchText
                                }
                                onChange={(
                                    event
                                ) =>
                                    setSearchText(
                                        event
                                            .target
                                            .value
                                    )
                                }
                            />
                        </div>

                        <div className="fees-filter-info">
                            <div className="fees-filter-info-icon">
                                <SettingOutlined />
                            </div>

                            <div>
                                <span>
                                    CURRENT VIEW
                                </span>

                                <strong>
                                    {filteredData.length}{" "}
                                    record
                                    {filteredData.length ===
                                    1
                                        ? ""
                                        : "s"}
                                </strong>
                            </div>
                        </div>

                        <div className="fees-filter-info">
                            <div className="fees-filter-info-icon">
                                <DollarOutlined />
                            </div>

                            <div>
                                <span>
                                    REGISTRATION TOTAL
                                </span>

                                <strong>
                                    {money(
                                        totalRegistration
                                    )}
                                </strong>
                            </div>
                        </div>
                    </div>
                </Card>

                <div className="fees-view-switch">
                    <button
                        type="button"
                        className={
                            activeTab ===
                            "records"
                                ? "active"
                                : ""
                        }
                        onClick={() =>
                            setActiveTab(
                                "records"
                            )
                        }
                    >
                        <DollarOutlined />
                        Fee Records
                    </button>

                    <button
                        type="button"
                        className={
                            activeTab ===
                            "analysis"
                                ? "active"
                                : ""
                        }
                        onClick={() =>
                            setActiveTab(
                                "analysis"
                            )
                        }
                    >
                        <BarChartOutlined />
                        Fee Analysis
                    </button>
                </div>

                {activeTab ===
                    "analysis" && (
                    <Card className="fees-chart-card">
                        <div className="fees-table-header">
                            <div>
                                <span className="fees-section-label">
                                    FINANCIAL OVERVIEW
                                </span>

                                <Title level={4}>
                                    Yearly Fee Analysis
                                </Title>

                                <Text type="secondary">
                                    Visual comparison of yearly
                                    school fees across the
                                    displayed fee structures.
                                </Text>
                            </div>

                            <Tag className="fees-record-count">
                                {chartData.length} Structures
                            </Tag>
                        </div>

                        <Divider />

                        {chartData.length ===
                        0 ? (
                            <Empty
                                description="No fee data available for analysis"
                                style={{
                                    padding:
                                        "70px 0"
                                }}
                            />
                        ) : (
                            <div
                                className="fees-chart"
                                style={{
                                    height:
                                        chartHeight
                                }}
                            >
                                <ResponsiveContainer
                                    width="100%"
                                    height="100%"
                                >
                                    <BarChart
                                        data={
                                            chartData
                                        }
                                        margin={{
                                            top: 20,
                                            right: 25,
                                            left: 10,
                                            bottom: 55
                                        }}
                                    >
                                        <CartesianGrid
                                            strokeDasharray="3 3"
                                            vertical={false}
                                        />

                                        <XAxis
                                            dataKey="name"
                                            angle={
                                                -30
                                            }
                                            textAnchor="end"
                                            interval={
                                                0
                                            }
                                            height={
                                                70
                                            }
                                            tick={{
                                                fontSize: 11
                                            }}
                                        />

                                        <YAxis
                                            tickFormatter={(
                                                value
                                            ) =>
                                                `$${Number(
                                                    value
                                                ).toLocaleString()}`
                                            }
                                            tick={{
                                                fontSize: 11
                                            }}
                                        />

                                        <RechartsTooltip
                                            formatter={(
                                                value
                                            ) =>
                                                [
                                                    money(
                                                        value
                                                    ),
                                                    "Yearly Fee"
                                                ]
                                            }
                                        />

                                        <Legend />

                                        <Bar
                                            dataKey="fee"
                                            name="Yearly Fee"
                                            radius={[
                                                8,
                                                8,
                                                0,
                                                0
                                            ]}
                                        >
                                            {chartData.map(
                                                (
                                                    _,
                                                    index
                                                ) => (
                                                    <Cell
                                                        key={
                                                            index
                                                        }
                                                        fill={
                                                            index %
                                                                2 ===
                                                            0
                                                                ? ORANGE
                                                                : NAVY
                                                        }
                                                    />
                                                )
                                            )}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </Card>
                )}

                {activeTab ===
                    "records" && (
                    <Card className="fees-table-card">
                        <div className="fees-table-header">
                            <div>
                                <span className="fees-section-label">
                                    FEE STRUCTURES
                                </span>

                                <Title level={4}>
                                    School Fees Records
                                </Title>

                                <Text type="secondary">
                                    Complete fee configuration
                                    for the selected academic
                                    year.
                                </Text>
                            </div>

                            <div className="fees-table-status">
                                <span className="fees-status-dot" />

                                {filteredData.length}{" "}
                                record
                                {filteredData.length ===
                                1
                                    ? ""
                                    : "s"}
                            </div>
                        </div>

                        <div className="fees-table-wrap">
                            <Table
                                loading={
                                    loading
                                }
                                columns={
                                    columns
                                }
                                dataSource={
                                    filteredData
                                }
                                rowKey={(
                                    record
                                ) =>
                                    record.id
                                }
                                pagination={{
                                    current:
                                        page,
                                    pageSize:
                                        rowsPerPage,
                                    total:
                                        filteredData.length,
                                    onChange:
                                        (
                                            newPage
                                        ) =>
                                            setPage(
                                                newPage
                                            ),
                                    showSizeChanger:
                                        false,
                                    showTotal:
                                        (
                                            total
                                        ) =>
                                            `Total ${total} records`
                                }}
                                scroll={{
                                    x: 1450
                                }}
                                size="middle"
                                rowClassName={() =>
                                    "professional-fee-row"
                                }
                                locale={{
                                    emptyText: (
                                        <Empty
                                            description="No school fee structures found"
                                        />
                                    )
                                }}
                            />
                        </div>
                    </Card>
                )}
            </div>

            <Modal
                open={
                    modalOpen
                }
                title={
                    <div className="fees-modal-title">
                        <div className="fees-modal-icon">
                            <DollarOutlined />
                        </div>

                        <div>
                            <strong>
                                {editData
                                    ? "Edit Fee Structure"
                                    : "Create Fee Structure"}
                            </strong>

                            <span>
                                {selectedYear?.name ||
                                    "No academic year selected"}
                            </span>
                        </div>
                    </div>
                }
                footer={null}
                onCancel={
                    closeModal
                }
                width="min(94vw, 900px)"
                centered
                destroyOnClose
                className="school-fees-modal"
            >
                <div className="fees-modal-body">
                    <Alert
                        type="info"
                        showIcon
                        message={
                            editData
                                ? "Editing an existing fee structure"
                                : "Create a new fee structure"
                        }
                        description={
                            editData
                                ? "Update the class, student category and installment schedule below."
                                : "Set the yearly school fee and installment schedule. The fourth installment is calculated automatically."
                        }
                        style={{
                            marginBottom: 22,
                            borderRadius: 12
                        }}
                    />

                    {renderFeeForm()}
                </div>
            </Modal>

            <style>
                {`
                    .school-fees-page {
                        min-height: 100vh;
                        padding: 22px;
                        background:
                            linear-gradient(
                                180deg,
                                #f6f8fb 0%,
                                #eef2f6 100%
                            );
                        color: #142536;
                    }

                    .school-fees-page *,
                    .school-fees-page *::before,
                    .school-fees-page *::after {
                        box-sizing: border-box;
                    }

                    .school-fees-shell {
                        width: 100%;
                        max-width: 1800px;
                        margin: 0 auto;
                    }

                    .fees-hero {
                        overflow: hidden;
                        border-radius: 24px;
                        background:
                            radial-gradient(
                                circle at 88% 10%,
                                rgba(246,201,68,.20),
                                transparent 28%
                            ),
                            linear-gradient(
                                135deg,
                                #062A4B 0%,
                                #063A6B 48%,
                                #0A0F12 100%
                            );
                        box-shadow:
                            0 18px 45px
                            rgba(6,42,75,.16);
                        margin-bottom: 18px;
                    }

                    .fees-hero-main {
                        min-height: 235px;
                        padding: 30px 32px;
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 30px;
                    }

                    .fees-hero-copy {
                        min-width: 0;
                    }

                    .fees-kicker {
                        display: flex;
                        align-items: center;
                        gap: 9px;
                        color: ${GOLD};
                        font-size: 11px;
                        font-weight: 900;
                        letter-spacing: 1.5px;
                        margin-bottom: 15px;
                    }

                    .fees-kicker-dot {
                        width: 8px;
                        height: 8px;
                        border-radius: 50%;
                        background: ${GOLD};
                        box-shadow:
                            0 0 0 5px
                            rgba(246,201,68,.12);
                    }

                    .fees-title-row {
                        display: flex;
                        align-items: center;
                        gap: 16px;
                    }

                    .fees-title-icon {
                        width: 58px;
                        height: 58px;
                        flex: 0 0 58px;
                        border-radius: 17px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        color: #062A4B;
                        background:
                            linear-gradient(
                                135deg,
                                #F6C944,
                                #EFA81C
                            );
                        font-size: 27px;
                        box-shadow:
                            0 10px 24px
                            rgba(0,0,0,.18);
                    }

                    .fees-title {
                        margin: 0 !important;
                        color: #ffffff !important;
                        font-size:
                            clamp(
                                24px,
                                3vw,
                                34px
                            ) !important;
                        line-height: 1.15 !important;
                        font-weight: 800 !important;
                        letter-spacing: -.5px;
                    }

                    .fees-subtitle {
                        display: block;
                        margin-top: 8px;
                        max-width: 720px;
                        color:
                            rgba(
                                255,
                                255,
                                255,
                                .72
                            ) !important;
                        font-size: 14px;
                        line-height: 1.65;
                    }

                    .fees-year-badge {
                        display: inline-flex;
                        align-items: center;
                        gap: 12px;
                        margin-top: 22px;
                        padding: 9px 13px;
                        border:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                .14
                            );
                        border-radius: 10px;
                        background:
                            rgba(
                                255,
                                255,
                                255,
                                .07
                            );
                    }

                    .fees-year-badge span {
                        color:
                            rgba(
                                255,
                                255,
                                255,
                                .52
                            );
                        font-size: 9px;
                        font-weight: 800;
                        letter-spacing: 1px;
                    }

                    .fees-year-badge strong {
                        color: ${GOLD};
                        font-size: 13px;
                    }

                    .fees-hero-actions {
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        gap: 10px;
                        flex-shrink: 0;
                    }

                    .fees-primary-button,
                    .fees-secondary-button {
                        height: 46px !important;
                        border-radius: 11px !important;
                        padding:
                            0 17px !important;
                        font-weight: 800 !important;
                    }

                    .fees-primary-button {
                        color: #062A4B !important;
                        background: ${GOLD} !important;
                        border-color: ${GOLD} !important;
                        box-shadow:
                            0 8px 20px
                            rgba(
                                246,
                                201,
                                68,
                                .18
                            );
                    }

                    .fees-primary-button:hover {
                        color: #062A4B !important;
                        background: #ffd95c !important;
                        border-color: #ffd95c !important;
                    }

                    .fees-secondary-button {
                        color: #ffffff !important;
                        background:
                            rgba(
                                255,
                                255,
                                255,
                                .08
                            ) !important;
                        border-color:
                            rgba(
                                255,
                                255,
                                255,
                                .22
                            ) !important;
                    }

                    .fees-secondary-button:hover {
                        color: #ffffff !important;
                        border-color: ${GOLD} !important;
                    }

                    .fees-hero-strip {
                        min-height: 70px;
                        display: grid;
                        grid-template-columns:
                            repeat(3, 1fr);
                        border-top:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                .10
                            );
                        background:
                            rgba(0,0,0,.16);
                    }

                    .fees-hero-strip > div {
                        display: flex;
                        flex-direction: column;
                        justify-content: center;
                        padding:
                            12px 28px;
                        border-right:
                            1px solid
                            rgba(
                                255,
                                255,
                                255,
                                .08
                            );
                    }

                    .fees-hero-strip > div:last-child {
                        border-right: 0;
                    }

                    .fees-hero-strip strong {
                        color: #ffffff;
                        font-size: 17px;
                        line-height: 1.2;
                    }

                    .fees-hero-strip span {
                        margin-top: 4px;
                        color:
                            rgba(
                                255,
                                255,
                                255,
                                .52
                            );
                        font-size: 10px;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: .8px;
                    }

                    .fees-summary-grid {
                        display: grid;
                        grid-template-columns:
                            repeat(
                                4,
                                minmax(0,1fr)
                            );
                        gap: 14px;
                        margin-bottom: 18px;
                    }

                    .fees-summary-card {
                        min-height: 122px;
                        border-radius: 17px !important;
                        border:
                            1px solid
                            #e3e9ee !important;
                        box-shadow:
                            0 8px 25px
                            rgba(
                                15,
                                23,
                                42,
                                .045
                            );
                    }

                    .fees-summary-card
                        .ant-card-body {
                        height: 100%;
                        display: flex;
                        align-items: center;
                        gap: 14px;
                        padding: 20px !important;
                    }

                    .fees-summary-icon {
                        width: 46px;
                        height: 46px;
                        flex: 0 0 46px;
                        border-radius: 14px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        font-size: 20px;
                    }

                    .fees-summary-icon.orange {
                        color: #C0750D;
                        background: #fff4e6;
                    }

                    .fees-summary-icon.blue {
                        color: #0F6DAB;
                        background: #edf7fd;
                    }

                    .fees-summary-icon.green {
                        color: #16803c;
                        background: #edf9f1;
                    }

                    .fees-summary-icon.gold {
                        color: #a46d00;
                        background: #fff8dc;
                    }

                    .fees-summary-content {
                        min-width: 0;
                        display: flex;
                        flex-direction: column;
                    }

                    .fees-summary-content span {
                        color: #6B7280;
                        font-size: 10px;
                        font-weight: 800;
                        text-transform: uppercase;
                        letter-spacing: .65px;
                    }

                    .fees-summary-content strong {
                        margin-top: 5px;
                        color: #142536;
                        font-size: 20px;
                        line-height: 1.2;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .fees-summary-content small {
                        margin-top: 4px;
                        color: #94a3b8;
                        font-size: 11px;
                    }

                    .fees-control-card,
                    .fees-chart-card,
                    .fees-table-card {
                        border-radius: 18px !important;
                        border:
                            1px solid
                            #e3e9ee !important;
                        box-shadow:
                            0 8px 25px
                            rgba(
                                15,
                                23,
                                42,
                                .045
                            );
                        margin-bottom: 18px;
                    }

                    .fees-control-card
                        .ant-card-body {
                        padding: 22px !important;
                    }

                    .fees-section-heading,
                    .fees-table-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        gap: 18px;
                    }

                    .fees-section-heading {
                        margin-bottom: 18px;
                    }

                    .fees-section-heading h4,
                    .fees-table-header h4 {
                        margin:
                            3px 0 2px !important;
                        color: #142536;
                        font-weight: 800;
                    }

                    .fees-section-label {
                        color: #C0750D;
                        font-size: 10px;
                        font-weight: 900;
                        letter-spacing: 1.15px;
                    }

                    .fees-control-actions {
                        display: flex;
                        align-items: center;
                        gap: 9px;
                    }

                    .fees-reset-button {
                        height: 44px !important;
                        border-radius: 10px !important;
                        font-weight: 700 !important;
                    }

                    .fees-create-button {
                        height: 44px !important;
                        border-radius: 10px !important;
                        background: ${NAVY} !important;
                        border-color: ${NAVY} !important;
                        font-weight: 800 !important;
                    }

                    .fees-filter-grid {
                        display: grid;
                        grid-template-columns:
                            minmax(
                                300px,
                                1.7fr
                            )
                            minmax(
                                190px,
                                .8fr
                            )
                            minmax(
                                190px,
                                .8fr
                            );
                        gap: 12px;
                    }

                    .fees-search-field
                        .ant-input-affix-wrapper {
                        min-height: 46px;
                        border-radius: 11px !important;
                        border-color:
                            #dbe3e9 !important;
                    }

                    .fees-search-field
                        .ant-input-prefix {
                        color: ${ORANGE};
                    }

                    .fees-filter-info {
                        min-height: 46px;
                        display: flex;
                        align-items: center;
                        gap: 11px;
                        padding:
                            8px 13px;
                        border:
                            1px solid
                            #e3e9ee;
                        border-radius: 11px;
                        background: #fafbfc;
                    }

                    .fees-filter-info-icon {
                        width: 32px;
                        height: 32px;
                        flex: 0 0 32px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 9px;
                        color: ${ORANGE};
                        background: #fff4e6;
                    }

                    .fees-filter-info > div:last-child {
                        min-width: 0;
                        display: flex;
                        flex-direction: column;
                    }

                    .fees-filter-info span {
                        color: #94a3b8;
                        font-size: 8px;
                        font-weight: 900;
                        letter-spacing: .8px;
                    }

                    .fees-filter-info strong {
                        color: #142536;
                        font-size: 13px;
                        margin-top: 2px;
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                    }

                    .fees-view-switch {
                        display: flex;
                        align-items: center;
                        gap: 6px;
                        width: fit-content;
                        margin:
                            4px 0 14px;
                        padding: 5px;
                        border:
                            1px solid
                            #e1e7ec;
                        border-radius: 12px;
                        background: #ffffff;
                        box-shadow:
                            0 5px 18px
                            rgba(
                                15,
                                23,
                                42,
                                .035
                            );
                    }

                    .fees-view-switch button {
                        border: 0;
                        background: transparent;
                        border-radius: 8px;
                        padding:
                            9px 15px;
                        display: inline-flex;
                        align-items: center;
                        gap: 7px;
                        color: #64748b;
                        font-size: 12px;
                        font-weight: 800;
                        cursor: pointer;
                        transition:
                            .2s ease;
                    }

                    .fees-view-switch button:hover {
                        color: ${ORANGE};
                    }

                    .fees-view-switch button.active {
                        color: #ffffff;
                        background: ${NAVY};
                        box-shadow:
                            0 5px 12px
                            rgba(
                                6,
                                42,
                                75,
                                .15
                            );
                    }

                    .fees-chart-card
                        .ant-card-body {
                        padding: 0 !important;
                    }

                    .fees-chart-card
                        .fees-table-header {
                        padding:
                            20px 22px;
                    }

                    .fees-chart-card
                        .ant-divider {
                        margin: 0;
                    }

                    .fees-chart {
                        width: 100%;
                        padding:
                            20px 22px 25px;
                    }

                    .fees-record-count {
                        margin: 0;
                        padding:
                            6px 11px;
                        color: #063A6B !important;
                        background: #edf5fa !important;
                        border:
                            1px solid
                            #d7e8f2 !important;
                        border-radius: 20px;
                        font-weight: 800;
                    }

                    .fees-table-card {
                        overflow: hidden;
                    }

                    .fees-table-card
                        .fees-table-header {
                        padding:
                            20px 22px;
                        border-bottom:
                            1px solid
                            #e7edf1;
                        background: #ffffff;
                    }

                    .fees-table-status {
                        display: inline-flex;
                        align-items: center;
                        gap: 7px;
                        padding:
                            7px 11px;
                        color: #475569;
                        background: #f7f9fa;
                        border:
                            1px solid
                            #e5eaee;
                        border-radius: 20px;
                        font-size: 11px;
                        font-weight: 700;
                    }

                    .fees-status-dot {
                        width: 7px;
                        height: 7px;
                        border-radius: 50%;
                        background: ${GREEN};
                        box-shadow:
                            0 0 0 4px
                            rgba(
                                22,
                                128,
                                60,
                                .10
                            );
                    }

                    .fees-table-wrap {
                        width: 100%;
                    }

                    .fees-table-wrap
                        .ant-table-thead
                        > tr
                        > th {
                        background:
                            ${NAVY} !important;
                        color:
                            #ffffff !important;
                        border-bottom:
                            0 !important;
                        padding-top:
                            14px !important;
                        padding-bottom:
                            14px !important;
                        font-size:
                            10px !important;
                        font-weight:
                            800 !important;
                        letter-spacing:
                            .55px;
                        white-space:
                            nowrap;
                    }

                    .fees-table-wrap
                        .ant-table-thead
                        > tr
                        > th::before {
                        display: none !important;
                    }

                    .fees-table-wrap
                        .ant-table-tbody
                        > tr
                        > td {
                        padding-top:
                            14px !important;
                        padding-bottom:
                            14px !important;
                        border-bottom-color:
                            #edf1f4 !important;
                    }

                    .professional-fee-row:hover
                        > td {
                        background:
                            #fff8eb !important;
                    }

                    .fees-table-wrap
                        .ant-pagination {
                        padding:
                            8px 18px 18px;
                    }

                    .fees-table-wrap
                        .ant-pagination-item-active {
                        border-color:
                            ${ORANGE} !important;
                    }

                    .fees-table-wrap
                        .ant-pagination-item-active
                        a {
                        color:
                            ${ORANGE} !important;
                    }

                    .fee-class-cell {
                        display: flex;
                        align-items: center;
                        gap: 10px;
                    }

                    .fee-class-icon {
                        width: 34px;
                        height: 34px;
                        flex: 0 0 34px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 10px;
                        color: ${NAVY};
                        background: #edf5fa;
                    }

                    .fee-class-cell > div:last-child {
                        min-width: 0;
                        display: flex;
                        flex-direction: column;
                    }

                    .fee-class-cell span {
                        margin-top: 2px;
                        color: #94a3b8;
                        font-size: 9px;
                        text-transform:
                            uppercase;
                        letter-spacing: .6px;
                    }

                    .fee-money-cell {
                        display: flex;
                        flex-direction: column;
                        gap: 3px;
                    }

                    .fee-money-cell span {
                        color: #94a3b8;
                        font-size: 9px;
                        text-transform:
                            uppercase;
                        letter-spacing: .6px;
                    }

                    .fee-money-cell strong {
                        color: #142536;
                        font-size: 14px;
                    }

                    .fee-edit-button {
                        background:
                            ${NAVY} !important;
                        border-color:
                            ${NAVY} !important;
                    }

                    .fee-edit-button:hover {
                        background:
                            #0a436e !important;
                        border-color:
                            #0a436e !important;
                    }

                    .school-fees-modal
                        .ant-modal-content {
                        padding: 0 !important;
                        overflow: hidden;
                        border-radius:
                            22px !important;
                    }

                    .school-fees-modal
                        .ant-modal-header {
                        padding:
                            20px 24px !important;
                        margin: 0 !important;
                        border-bottom:
                            1px solid
                            #edf1f4 !important;
                    }

                    .fees-modal-title {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .fees-modal-icon {
                        width: 45px;
                        height: 45px;
                        flex: 0 0 45px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 13px;
                        color: #062A4B;
                        background:
                            linear-gradient(
                                135deg,
                                #F6C944,
                                #EFA81C
                            );
                        font-size: 20px;
                    }

                    .fees-modal-title > div:last-child {
                        display: flex;
                        flex-direction: column;
                    }

                    .fees-modal-title strong {
                        color: #142536;
                        font-size: 17px;
                        line-height: 1.2;
                    }

                    .fees-modal-title span {
                        margin-top: 4px;
                        color: #94a3b8;
                        font-size: 11px;
                        font-weight: 500;
                    }

                    .fees-modal-body {
                        max-height: 76vh;
                        overflow-y: auto;
                        padding:
                            22px 24px 24px;
                    }

                    .fee-form-section {
                        margin-bottom: 8px;
                    }

                    .fee-form-section-title {
                        display: flex;
                        align-items: center;
                        gap: 9px;
                        margin:
                            3px 0 17px;
                        color: ${NAVY};
                        font-size: 12px;
                        font-weight: 900;
                        letter-spacing: .8px;
                        text-transform:
                            uppercase;
                    }

                    .fee-form-section-title span {
                        width: 28px;
                        height: 28px;
                        display: inline-flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 8px;
                        color: ${NAVY};
                        background:
                            #f8e9b0;
                        font-size: 9px;
                    }

                    .school-fees-modal
                        .ant-form-item {
                        margin-bottom:
                            17px;
                    }

                    .school-fees-modal
                        .ant-form-item-label
                        > label {
                        color:
                            #334155;
                        font-size:
                            12px;
                        font-weight:
                            750;
                    }

                    .school-fees-modal
                        .ant-input-number,
                    .school-fees-modal
                        .ant-select-selector {
                        min-height:
                            45px !important;
                        border-radius:
                            10px !important;
                        border-color:
                            #dbe3e9 !important;
                    }

                    .school-fees-modal
                        .ant-input-number-input {
                        height: 43px;
                    }

                    .school-fees-modal
                        .ant-input-number-focused,
                    .school-fees-modal
                        .ant-select-focused
                        .ant-select-selector {
                        border-color:
                            ${ORANGE} !important;
                        box-shadow:
                            0 0 0 2px
                            rgba(
                                255,
                                121,
                                0,
                                .10
                            ) !important;
                    }

                    .fee-installment-grid {
                        display: grid;
                        grid-template-columns:
                            repeat(
                                4,
                                minmax(0,1fr)
                            );
                        gap: 14px;
                    }

                    .fee-calculation-note {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                        padding:
                            13px 15px;
                        margin:
                            3px 0 20px;
                        border:
                            1px solid
                            #d8e9df;
                        border-radius: 12px;
                        background:
                            #f1faf4;
                    }

                    .fee-calculation-icon {
                        width: 35px;
                        height: 35px;
                        flex: 0 0 35px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        border-radius: 10px;
                        color: ${GREEN};
                        background:
                            #dff4e5;
                    }

                    .fee-calculation-note > div:last-child {
                        display: flex;
                        flex-direction: column;
                    }

                    .fee-calculation-note strong {
                        color:
                            #166534;
                        font-size:
                            12px;
                    }

                    .fee-calculation-note span {
                        margin-top: 3px;
                        color:
                            #64748b;
                        font-size:
                            11px;
                        line-height:
                            1.5;
                    }

                    .fee-form-actions {
                        position: sticky;
                        bottom: -24px;
                        z-index: 5;
                        display: flex;
                        justify-content: flex-end;
                        gap: 10px;
                        margin:
                            8px -24px -24px;
                        padding:
                            15px 24px;
                        border-top:
                            1px solid
                            #e8edf1;
                        background:
                            rgba(
                                255,
                                255,
                                255,
                                .97
                            );
                        backdrop-filter:
                            blur(10px);
                    }

                    .fee-cancel-button,
                    .fee-save-button {
                        min-height:
                            44px !important;
                        border-radius:
                            10px !important;
                        padding:
                            0 18px !important;
                        font-weight:
                            800 !important;
                    }

                    .fee-save-button {
                        background:
                            ${ORANGE} !important;
                        border-color:
                            ${ORANGE} !important;
                    }

                    .fee-save-button:hover {
                        background:
                            #d96500 !important;
                        border-color:
                            #d96500 !important;
                    }

                    @media (max-width: 1150px) {
                        .fees-summary-grid {
                            grid-template-columns:
                                repeat(
                                    2,
                                    minmax(0,1fr)
                                );
                        }

                        .fees-filter-grid {
                            grid-template-columns:
                                1fr 1fr;
                        }

                        .fees-search-field {
                            grid-column:
                                1 / -1;
                        }

                        .fee-installment-grid {
                            grid-template-columns:
                                repeat(
                                    2,
                                    minmax(0,1fr)
                                );
                        }
                    }

                    @media (max-width: 780px) {
                        .school-fees-page {
                            padding: 10px;
                        }

                        .fees-hero {
                            border-radius:
                                18px;
                        }

                        .fees-hero-main {
                            min-height: 0;
                            padding:
                                22px 18px;
                            flex-direction:
                                column;
                            align-items:
                                flex-start;
                            gap: 22px;
                        }

                        .fees-title-row {
                            align-items:
                                flex-start;
                        }

                        .fees-title-icon {
                            width: 48px;
                            height: 48px;
                            flex-basis: 48px;
                            border-radius:
                                14px;
                            font-size:
                                22px;
                        }

                        .fees-title {
                            font-size:
                                23px !important;
                        }

                        .fees-subtitle {
                            font-size:
                                12px;
                        }

                        .fees-year-badge {
                            width: 100%;
                            justify-content:
                                space-between;
                        }

                        .fees-hero-actions {
                            width: 100%;
                            display: grid;
                            grid-template-columns:
                                1fr 1fr;
                        }

                        .fees-hero-actions button {
                            width: 100%;
                        }

                        .fees-hero-strip {
                            grid-template-columns:
                                1fr;
                        }

                        .fees-hero-strip > div {
                            border-right: 0;
                            border-bottom:
                                1px solid
                                rgba(
                                    255,
                                    255,
                                    255,
                                    .08
                                );
                            padding:
                                11px 18px;
                        }

                        .fees-hero-strip > div:last-child {
                            border-bottom: 0;
                        }

                        .fees-summary-grid {
                            grid-template-columns:
                                1fr;
                        }

                        .fees-control-card
                            .ant-card-body {
                            padding:
                                16px !important;
                        }

                        .fees-section-heading,
                        .fees-table-header {
                            align-items:
                                flex-start;
                            flex-direction:
                                column;
                        }

                        .fees-control-actions {
                            width: 100%;
                        }

                        .fees-control-actions button {
                            flex: 1;
                        }

                        .fees-filter-grid {
                            grid-template-columns:
                                1fr;
                        }

                        .fees-search-field {
                            grid-column:
                                auto;
                        }

                        .fees-view-switch {
                            width: 100%;
                        }

                        .fees-view-switch button {
                            flex: 1;
                            justify-content:
                                center;
                        }

                        .fees-table-card
                            .fees-table-header {
                            padding:
                                17px;
                        }

                        .fees-chart-card
                            .fees-table-header {
                            padding:
                                17px;
                        }

                        .fees-chart {
                            padding:
                                15px;
                        }

                        .school-fees-modal
                            .ant-modal-header {
                            padding:
                                18px 16px !important;
                        }

                        .fees-modal-body {
                            max-height:
                                74vh;
                            padding:
                                18px 16px 16px;
                        }

                        .fee-installment-grid {
                            grid-template-columns:
                                1fr;
                            gap: 0;
                        }

                        .fee-form-actions {
                            margin:
                                8px -16px -16px;
                            padding:
                                14px 16px;
                        }

                        .fee-form-actions button {
                            flex: 1;
                        }
                    }

                    @media (max-width: 480px) {
                        .fees-title-row {
                            gap: 11px;
                        }

                        .fees-title-icon {
                            width: 43px;
                            height: 43px;
                            flex-basis: 43px;
                            font-size: 19px;
                        }

                        .fees-title {
                            font-size:
                                20px !important;
                        }

                        .fees-hero-actions {
                            grid-template-columns:
                                1fr;
                        }

                        .fees-summary-content strong {
                            font-size:
                                18px;
                        }

                        .fees-filter-info {
                            min-height:
                                50px;
                        }

                        .fee-form-actions {
                            flex-direction:
                                column-reverse;
                        }

                        .fee-form-actions button {
                            width: 100%;
                        }
                    }

                    @media (prefers-reduced-motion: reduce) {
                        .school-fees-page * {
                            transition:
                                none !important;
                            scroll-behavior:
                                auto !important;
                        }
                    }
                `}
            </style>
        </div>
    );
};

export default SchoolFeesData;